import React from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { WizardStep } from '@/store/hook-track/types';

interface HookTrackWizardStepperProps {
  steps: WizardStep[];
  currentStep: number;
  onStepClick: (stepId: number) => void;
}

export function HookTrackWizardStepper({ steps, currentStep, onStepClick }: HookTrackWizardStepperProps) {
  const isStepAccessible = (stepId: number) => {
    if (stepId === currentStep) return true;
    if (steps[stepId - 1]?.completed) return true;
    if (stepId === currentStep - 1) return true;
    return false;
  };

  return (
    <div className="relative lg:sticky lg:top-0 z-20 dark:bg-background border-border/50">
      {/* Mobile: Horizontal Scrollable Chips */}
      <div className="lg:hidden overflow-x-auto scrollbar-hide">
        <div className="flex items-center justify-start gap-1 px-4 py-3 min-w-max">
          {steps.map((step, index) => {
            const isActive = step.id === currentStep;
            const isCompleted = step.completed;
            const isAccessible = isStepAccessible(step.id);
            const isLast = index === steps.length - 1;
            
            return (
              <React.Fragment key={step.id}>
                <button
                  onClick={() => isAccessible && onStepClick(step.id)}
                  disabled={!isAccessible}
                  className={cn(
                    "flex items-center gap-2 px-3 py-1.5 rounded-full transition-all duration-200",
                    isActive && "bg-[#1D4FFF] shadow-sm",
                    !isActive && "bg-transparent",
                    !isAccessible && "opacity-40 cursor-not-allowed"
                  )}
                >
                  {/* Dot or Check */}
                  <div className={cn(
                    "flex items-center justify-center transition-all duration-300",
                    isCompleted && "w-4 h-4 rounded-full bg-[#1D4FFF]",
                    isActive && !isCompleted && "w-5 h-5 rounded-full bg-white",
                    !isActive && !isCompleted && "w-[5px] h-[5px] rounded-full bg-[#D1D5DB]"
                  )}>
                    {(isCompleted || isActive) && (
                      <Check 
                        className={cn(
                          "w-2.5 h-2.5",
                          isActive && !isCompleted ? "text-[#1D4FFF]" : "text-white"
                        )} 
                        strokeWidth={3} 
                      />
                    )}
                  </div>
                  
                  {/* Title */}
                  <span className={cn(
                    "text-[12px] font-medium whitespace-nowrap transition-colors duration-200",
                    isActive && "text-white",
                    isCompleted && !isActive && "text-[#1D4FFF]",
                    !isActive && !isCompleted && "text-[#9CA3AF]"
                  )}>
                    {step.title}
                  </span>
                </button>

                {/* Progress Line */}
                {!isLast && (
                  <div className="w-6 h-[2px] bg-[#E5E7EB] rounded-full overflow-hidden mx-0.5">
                    <div className={cn(
                      "h-full bg-[#1D4FFF] transition-all duration-500 ease-out",
                      isCompleted ? "w-full" : "w-0"
                    )} />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Desktop: Premium Stepper */}
      <div className="hidden lg:block">
        <div className="container mx-auto px-4 lg:px-16 py-3">
          <div className="flex items-center justify-center">
            {steps.map((step, index) => {
              const isActive = step.id === currentStep;
              const isCompleted = step.completed;
              const isAccessible = isStepAccessible(step.id);
              const isLast = index === steps.length - 1;

              return (
                <React.Fragment key={step.id}>
                  <button
                    onClick={() => isAccessible && onStepClick(step.id)}
                    disabled={!isAccessible}
                  className={cn(
                      "group flex items-center gap-2.5 px-4 py-2 rounded-full transition-all duration-200",
                      isActive && "bg-[#1D4FFF] shadow-[0_4px_12px_rgba(29,79,255,0.25)]",
                      !isActive && isAccessible && "hover:bg-white/60",
                      !isAccessible && "cursor-not-allowed opacity-40"
                    )}
                  >
                    {/* Dot or Checkmark */}
                    <div className={cn(
                      "flex items-center justify-center transition-all duration-300",
                      isCompleted && "w-5 h-5 rounded-full bg-[#1D4FFF]",
                      isActive && !isCompleted && "w-5 h-5 rounded-full bg-white shadow-[0_0_0_3px_rgba(255,255,255,0.25)]",
                      !isActive && !isCompleted && "w-[6px] h-[6px] rounded-full bg-[#D1D5DB]"
                    )}>
                      {(isCompleted || isActive) && (
                        <Check 
                          className={cn(
                            "w-3 h-3",
                            isActive && !isCompleted ? "text-[#1D4FFF]" : "text-white"
                          )} 
                          strokeWidth={3} 
                        />
                      )}
                    </div>

                    {/* Title */}
                    <span className={cn(
                      "text-[13px] font-medium transition-colors duration-200",
                      isActive && "text-white",
                      isCompleted && !isActive && "text-[#1D4FFF]",
                      !isActive && !isCompleted && "text-[#9CA3AF]"
                    )}>
                      {step.title}
                    </span>
                  </button>

                  {/* Progress Line */}
                  {!isLast && (
                    <div className="w-12 xl:w-16 h-[2px] bg-[#E5E7EB] rounded-full overflow-hidden mx-1">
                      <div className={cn(
                        "h-full bg-[#1D4FFF] transition-all duration-500 ease-out",
                        isCompleted ? "w-full" : "w-0"
                      )} />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
